import {
  ComplianceDocument,
  MarketingAssetSchema,
} from '@/lib/types';
import { styled, Theme } from '@mui/material/styles';
import FileIcon from './FileIcon';
import { FormattedMessage } from 'react-intl';
import { Fragment, ReactNode } from 'react';
import Languages from '@/constants/Languages';
import Button from '@/components/Button/Button';
import DownloadIcon from '@/assets/download.svg?react';
import Checkbox from '@/components/Checkbox/Checkbox';
import RadioButton from '@/components/RadioButton/RadioButton';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters } from '@/lib/api';
import { useDownload } from '@/hooks/useDownload';
import Date from '@/components/Date/Date';
import FileType from './FileType';
import {
  getFileGames,
  getFileMarkets,
  groupFileGames,
} from './FileExplorerHelpers';
import FileGamesTooltip from './FileGamesTooltip';
import ResilientImage from '@/components/ResilientImage/ResilientImage';
import { isExactNameMatch } from '@/lib/searchMatch';
import ExactMatchBadge from './ExactMatchBadge';
import ComplianceThumbnail from './ComplianceThumbnail';

const FileGridItem = ({
  file,
  selected,
  type = 'compliance',
  onChange,
  selectionMode = 'checkbox',
  onOpen,
  onDownload,
  downloading,
  exactMatchQuery,
}: {
  file: ComplianceDocument | MarketingAssetSchema;
  selected?: boolean;
  type?: 'compliance' | 'marketing' | 'game';
  onChange?: (id: number) => void;
  selectionMode?: 'checkbox' | 'radio';
  /** Called when the file itself is acted on (opened or downloaded). */
  onOpen?: (source: 'download' | 'preview') => void;
  onDownload?: () => void;
  downloading?: boolean;
  exactMatchQuery?: string;
}) => {
  const { data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const { download, loading } = useDownload();
  const marketItems = getFileMarkets(data?.markets || [], file);
  const marketsLabel = marketItems.length
    ? marketItems.map((market) => market.text).join(', ')
    : null;
  const languageElements = file.language
    ? Languages?.[file.language.trim().toLowerCase()]
    : null;
  const showLanguage = file.language;
  const showPreview = type === 'marketing';
  const gameRows = getFileGames(data?.games, file);
  const gamesLabel = gameRows.length
    ? gameRows.map(({ name }) => name).join(', ')
    : null;
  const gameGroups = groupFileGames(gameRows);

  type DetailField = {
    key: string;
    label: ReactNode;
    value: ReactNode;
  };

  const fileTypeField: DetailField = {
    key: 'fileType',
    label: (
      <FormattedMessage id="fileType" defaultMessage="File Type" />
    ),
    value: (
      <LabelInfo aria-label="File type">
        <FileType file={file} />
      </LabelInfo>
    ),
  };

  const languageField: DetailField | null = showLanguage
    ? {
        key: 'language',
        label: (
          <FormattedMessage id="language" defaultMessage="Language" />
        ),
        value: (
          <LabelInfo aria-label="Language">
            {languageElements?.icon ? (
              <LanguageIcon>{languageElements.icon}</LanguageIcon>
            ) : null}
            {languageElements
              ? languageElements.label
              : file.language}
          </LabelInfo>
        ),
      }
    : null;

  const marketField: DetailField | null = marketsLabel
    ? {
        key: 'market',
        label: (
          <FormattedMessage id="market" defaultMessage="Market" />
        ),
        value: (
          <MarketLabelInfo aria-label="Market" title={marketsLabel}>
            {marketItems.map(({ key, icon }) =>
              icon ? (
                <LanguageIcon key={key}>{icon}</LanguageIcon>
              ) : null,
            )}
            <span>{marketsLabel}</span>
          </MarketLabelInfo>
        ),
      }
    : null;

  const gamesField: DetailField | null = gamesLabel
    ? {
        key: 'games',
        label: <FormattedMessage id="games" defaultMessage="Games" />,
        value: (
          <FileGamesTooltip groups={gameGroups}>
            <LabelInfoEllipsis aria-label="Games">
              {gamesLabel}
            </LabelInfoEllipsis>
          </FileGamesTooltip>
        ),
      }
    : null;

  // Fixed slots — File Type and Language, then Market and Games —
  // so a field sits in the same place on every card. Games is cut
  // here and read in full from its tooltip, so it needs no row of its
  // own. Rows and cells with nothing in them are dropped.
  const detailRows = [
    [fileTypeField, languageField],
    [marketField, gamesField],
  ]
    .map((row) => row.filter((field) => field !== null))
    .filter((row) => row.length > 0);

  const handleDownloadClick = () => {
    onOpen?.('download');
    if (onDownload) {
      onDownload();
      return;
    }
    download({
      category:
        type === 'compliance'
          ? 'compliance_documents'
          : 'marketing_assets',
      file_ids: [file.id],
      entity_name: file.name,
      extension: file.extension,
    });
  };

  return (
    <Root>
      <Content>
        {showPreview ? (
          <PreviewImage
            src={(file as MarketingAssetSchema)?.preview_url}
            alt={file.name}
            aria-label="File preview"
          />
        ) : null}
        {type === 'compliance' ? (
          <CompliancePreview file={file as ComplianceDocument} />
        ) : null}
        <MainInfo>
          <StyledFileIcon fileType={file.extension} size={32} />
          <MainInfoText>
            <FileName aria-label="File name" title={file.name}>
              {file.extension === 'pdf' &&
              (file as ComplianceDocument)?.preview_url ? (
                <FileNameLink
                  href={
                    (file as ComplianceDocument).preview_url as string
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title={file.name}
                  onClick={() => onOpen?.('preview')}
                >
                  {file.name}
                </FileNameLink>
              ) : (
                file.name
              )}
            </FileName>
            <FileDetails>
              {/* <span aria-label="File size">
                {(file as ComplianceDocument)?.size ||
                  (file as MarketingAssetSchema)?.size}
              </span>
              <FileDetailsDot /> */}
              <span aria-label="File updated at">
                <Date
                  value={(file as ComplianceDocument)?.updated_at}
                />
              </span>
              {exactMatchQuery &&
              isExactNameMatch(
                file.name,
                exactMatchQuery,
                file.extension,
              ) ? (
                <ExactMatchBadge />
              ) : null}
            </FileDetails>
          </MainInfoText>
        </MainInfo>
        {detailRows.map((row) => (
          <DetailRow key={row.map(({ key }) => key).join('-')}>
            {row.map(({ key, label, value }, index) => (
              <Fragment key={key}>
                {index > 0 ? <Divider /> : null}
                <DetailRowSection>
                  <Label>{label}</Label>
                  {value}
                </DetailRowSection>
              </Fragment>
            ))}
          </DetailRow>
        ))}
      </Content>
      <Actions>
        <StyledButton
          variant="text"
          onClick={handleDownloadClick}
          aria-label="Download"
          loading={onDownload ? !!downloading : loading}
        >
          <DownloadIcon />
          <FormattedMessage id="download" defaultMessage="Download" />
        </StyledButton>
        {onChange && (
          <div>
            {selectionMode === 'radio' ? (
              <RadioButton
                checked={!!selected}
                onChange={() => onChange(file.id)}
                id={`select-${type}-${file.id}`}
                aria-label="Select"
              />
            ) : (
              <Checkbox
                checked={!!selected}
                onChange={() => onChange(file.id)}
                id={`select-${type}-${file.id}`}
                aria-label="Select"
              />
            )}
            <CheckBoxLabel htmlFor={`select-${type}-${file.id}`}>
              <FormattedMessage id="select" defaultMessage="Select" />
            </CheckBoxLabel>
          </div>
        )}
      </Actions>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  width: 280,
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  borderRadius: theme.radii.xs,
  backgroundColor: theme.palette.white,
  boxShadow:
    '0px 10px 32px -4px #1319271A, 0px 6px 14px -6px #1319271F',
}));

const Content = styled('div')(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const previewBox = (theme: Theme) => ({
  margin: `${theme.spacing(2)} ${theme.spacing(2)} 0`,
  width: 'calc(100% - 32px)',
  height: 120,
  borderRadius: theme.radii.xxs,
});

const PreviewImage = styled(ResilientImage)(({ theme }) => ({
  ...previewBox(theme),
  objectFit: 'cover',

  svg: {
    width: 50,
    height: 40,
  },
}));

const CompliancePreview = styled(ComplianceThumbnail)(({ theme }) =>
  previewBox(theme),
);

const MainInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  margin: theme.spacing(2),
}));

const StyledFileIcon = styled(FileIcon)({
  height: 40,
  width: 32,
  flexShrink: 0,
  marginRight: 10,
});

const MainInfoText = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  width: 'calc(100% - 42px)',
}));

const FileName = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.palette.textPrimaryBlack,
  marginBottom: 5,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  width: '100%',
}));

const FileNameLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
}));

const FileDetails = styled('div')(({ theme }) => ({
  color: theme.palette.textSecondaryDarkGrey,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}));

// const FileDetailsDot = styled('div')(({ theme }) => ({
//   background: theme.palette.iconGrey,
//   width: 5,
//   height: 5,
//   borderRadius: '50%',
// }));

const Label = styled('div')(({ theme }) => ({
  color: theme.palette.textSecondaryDarkGrey,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16px',
  margin: `0 ${theme.spacing(2)} ${theme.spacing(0.5)}`,
}));

const LabelInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  color: theme.palette.textPrimaryBlack,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  margin: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
}));

const LabelInfoEllipsis = styled('div')(({ theme }) => ({
  color: theme.palette.textPrimaryBlack,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  margin: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const MarketLabelInfo = styled(LabelInfo)(() => ({
  minWidth: 0,
  overflow: 'hidden',

  span: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
}));

const DetailRowSection = styled('div')(() => ({
  flex: 1,
  minWidth: 0,
}));

const LanguageIcon = styled('div')(() => ({
  width: 16,
  height: 16,
  display: 'block',

  svg: {
    width: '100%',
    height: '100%',
  },
}));

const DetailRow = styled('div')(() => ({
  display: 'flex',
}));

const Divider = styled('div')(({ theme }) => ({
  width: 1,
  height: '39px',
  background: theme.palette.grey200,
}));

const Actions = styled('div')(({ theme }) => ({
  background: theme.palette.grey100,
  padding: `5px 0`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottomLeftRadius: theme.radii.xs,
  borderBottomRightRadius: theme.radii.xs,
}));

const StyledButton = styled(Button)(() => ({
  fontWeight: 500,
  padding: '5px 15px',
}));

const CheckBoxLabel = styled('label')(({ theme }) => ({
  color: theme.palette.textPrimaryBlack,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  paddingLeft: 4,
  marginRight: 16,
  cursor: 'pointer',
  userSelect: 'none',
}));

export default FileGridItem;
